import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Calendar, MapPin, Car, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 6;

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [booking, setBooking] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (attempt >= MAX_ATTEMPTS) {
      setState("timeout");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setBooking(data.booking);
        setState("paid");
        return;
      }
      if (data.status === "expired") {
        setState("failed");
        return;
      }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  return (
    <main data-testid="booking-success-page" className="pt-32 pb-24 min-h-screen flex items-center">
      <div className="max-w-xl mx-auto px-5 sm:px-8 w-full text-center">
        {state === "checking" && (
          <div data-testid="status-checking">
            <Loader2 className="w-14 h-14 text-yellow-400 animate-spin mx-auto mb-6" />
            <h1 className="font-heading font-bold text-2xl text-white uppercase">Confirming your deposit…</h1>
            <p className="text-neutral-400 mt-3">Hang tight while we verify your payment with Stripe.</p>
          </div>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="status-paid">
            <div className="w-20 h-20 bg-yellow-400 rounded-sm flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-11 h-11 text-black" />
            </div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tighter text-white">Booking Confirmed!</h1>
            <p className="text-neutral-400 mt-3">Your slot is locked in. A confirmation has been sent to <span className="text-yellow-400">{booking?.customer_email}</span>.</p>

            {booking && (
              <div className="mt-8 bg-[#141414] border border-neutral-800 rounded-sm p-6 text-left space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-neutral-900 border border-neutral-800 rounded-sm flex items-center justify-center"><Calendar className="w-5 h-5 text-yellow-400" /></div>
                  <div>
                    <p className="font-bold text-white">{booking.service_name}</p>
                    <p className="text-sm text-neutral-400">{booking.date} · {booking.time_slot}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-neutral-900 border border-neutral-800 rounded-sm flex items-center justify-center"><Car className="w-5 h-5 text-yellow-400" /></div>
                  <p className="text-sm text-neutral-300">{booking.vehicle_year} {booking.vehicle_make} {booking.vehicle_model}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-neutral-900 border border-neutral-800 rounded-sm flex items-center justify-center"><MapPin className="w-5 h-5 text-yellow-400" /></div>
                  <p className="text-sm text-neutral-300">{booking.address}</p>
                </div>
                <div className="border-t border-neutral-800 pt-4 flex justify-between text-sm">
                  <span className="text-neutral-500">Deposit paid</span>
                  <span className="font-mono text-yellow-400 font-semibold">${booking.deposit_amount?.toFixed(2)}</span>
                </div>
              </div>
            )}

            <Link to="/" data-testid="back-home-button" className="mt-8 inline-flex items-center gap-2 bg-yellow-400 text-black font-bold uppercase tracking-wide px-8 py-3.5 rounded-sm hover:bg-yellow-500 transition-colors">
              Back to Home <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        )}

        {(state === "failed" || state === "timeout") && (
          <div data-testid="status-failed">
            <div className="w-20 h-20 bg-neutral-900 border border-neutral-800 rounded-sm flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-11 h-11 text-red-500" />
            </div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl uppercase text-white">
              {state === "timeout" ? "Still Processing" : "Payment Not Completed"}
            </h1>
            <p className="text-neutral-400 mt-3">
              {state === "timeout"
                ? "We couldn't confirm your payment yet. If you were charged, you'll receive a confirmation email shortly."
                : "Your deposit wasn't completed, so no slot was reserved. You can try booking again."}
            </p>
            <button onClick={() => navigate("/book")} data-testid="retry-booking-button" className="mt-8 inline-flex items-center gap-2 bg-yellow-400 text-black font-bold uppercase tracking-wide px-8 py-3.5 rounded-sm hover:bg-yellow-500 transition-colors">
              Try Again <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
